import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import BottomSheetModal from "./BottomSheetModal";
import { Divider, NumberedSteps } from "./SheetPrimitives";

import ticketIcon from "../../assets/images/wallet/ticket-star.svg";
import copyIcon from "../../assets/images/wallet/copy.svg";
import qrFallback from "../../assets/images/wallet/qr-border.svg";

import { get } from "../../api/axios";
import endpoints from "../../api/endpoints";
import { extractErrorMessage } from "../../utils/errorHandler";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * VoucherDetailModal — shows a single wallet voucher: amount, expiry, QR
 * code, copyable voucher ID, redemption steps, and legal disclaimer.
 * Built on the shared BottomSheetModal shell.
 * ---------------------------------------------------------------------- */

const DEFAULT_STEPS = [
  "Present this QR code at the restaurant counter.",
  "Ask the cashier to scan.",
  "Discount is applied automatically at checkout.",
];

const VoucherDetailModal = ({ voucherId, isOpen, onClose, onRestaurantClick }) => {
  const [copied, setCopied] = useState(false);
  const [qrError, setQrError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voucher, setVoucher] = useState(null);

  const fetchVoucherDetails = useCallback(async () => {
    if (!voucherId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await get(endpoints.voucher.replace("1", voucherId));
      
      if (response.data.success) {
        const voucherData = response.data.data.voucher;

        // Format expiry date based on voucher status
        let expiresLabel = "Soon";
        let expiresSubLabel = "";
        let showExpiryNote = true;
        const now = new Date();
        const status = voucherData.status?.toLowerCase();

        if (status === "used" || status === "redeemed") {
          // Redeemed voucher - show redeemed date
          const redeemedDate = new Date(voucherData.redeemed_at);
          expiresLabel = redeemedDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          expiresSubLabel = "Redeemed on";
          showExpiryNote = false;
        } else if (status === "expired" || (voucherData.expires_at && new Date(voucherData.expires_at) < now)) {
          // Expired voucher - show expired date
          const expiryDate = new Date(voucherData.expires_at);
          expiresLabel = expiryDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          expiresSubLabel = "Expired on";
          showExpiryNote = false;
        } else if (voucherData.expires_at) {
          // Active voucher - show expires date
          const expiryDate = new Date(voucherData.expires_at);
          expiresLabel = expiryDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          expiresSubLabel = "Expires";
        }

        // Transform API response to match component's expected format
        const transformedVoucher = {
          amount: voucherData.amount_cents ? voucherData.amount_cents / 100 : voucherData.amount || 0,
          expiresLabel,
          expiresSubLabel,
          showExpiryNote,
          qrCodeUrl: voucherData.qr_url || voucherData.qr_code_url,
          qrPayload: voucherData.qr_payload,
          restaurantName: voucherData.vendor_listing?.title || voucherData.restaurant_name || "Restaurant",
          voucherId: voucherData.code || voucherData.id?.toString(),
          steps: DEFAULT_STEPS,
          disclaimer: "Single use only · Cannot be combined with other offers · Non-transferable",
        };

        setVoucher(transformedVoucher);
      } else {
        const errorMessage = response.data.message || "Failed to load voucher details";
        setError(errorMessage);
        toastr.error(errorMessage);
      }
    } catch (err) {
      let errorMessage = "Failed to load voucher details";
      
      // Handle specific error cases
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = "Voucher not found. It may have been deleted or expired.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to view this voucher.";
        } else if (err.response.status === 401) {
          errorMessage = "Please log in to view voucher details.";
        } else {
          errorMessage = err.response.data?.message || extractErrorMessage(err);
        }
      } else {
        errorMessage = extractErrorMessage(err);
      }
      
      setError(errorMessage);
      toastr.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [voucherId]);

  useEffect(() => {
    if (isOpen && voucherId) {
      fetchVoucherDetails();
    }
  }, [isOpen, voucherId, fetchVoucherDetails]);

  if (!isOpen) return null;
  if (loading) {
    return (
      <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="vdm-title">
        <div style={{ padding: "40px", textAlign: "center" }}>Loading voucher details...</div>
      </BottomSheetModal>
    );
  }
  if (error) {
    return (
      <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="vdm-title">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p style={{ color: "var(--text-red)" }}>{error}</p>
        </div>
      </BottomSheetModal>
    );
  }
  if (!voucher) return null;

  const {
    amount,
    expiresLabel,
    expiresSubLabel,
    showExpiryNote,
    qrCodeUrl,
    restaurantName,
    voucherId: voucherCode,
    steps = DEFAULT_STEPS,
    disclaimer = "Single use only \u00B7 Cannot be combined with other offers \u00B7 Non-transferable",
  } = voucher;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(voucherCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // clipboard API unavailable — fail silently, ID is still selectable
    }
  };

  const handleQrError = () => {
    setQrError(true);
  };

  return (
    <BottomSheetModal isOpen={isOpen} onClose={onClose} labelledBy="vdm-title">
      <div className="vdm-header">
        <div className="vdm-header__left">
          <span className="vdm-icon">
            <img src={ticketIcon} alt="" />
          </span>
          <span className="vdm-amount" id="vdm-title">
            ${amount}
          </span>
        </div>
        <div className="vdm-header__right">
          <span className="mc-pill mc-pill--muted">{expiresSubLabel} {expiresLabel}</span>
          {showExpiryNote && <span className="vdm-expiry-sub">Expires 1 year from issue date</span>}
        </div>
      </div>

      <Divider />

      <div className="mc-qr">
        <img 
          src={qrError ? qrFallback : qrCodeUrl} 
          alt="Voucher QR code" 
          onError={handleQrError}
        />
      </div>

      <p className="vdm-valid-at">
        Valid at: {restaurantName}
      </p><span className="vdm-valid-at__link"></span>

      <p className="mc-section-label">Voucher ID</p>
      <div className="mc-copy-field">
        <span className="mc-copy-field__value">{voucherCode}</span>
        <button
          type="button"
          className="mc-copy-field__btn"
          onClick={handleCopy}
          aria-label="Copy voucher ID"
        >
          <img src={copyIcon} alt="" />
        </button>
      </div>
      {copied && <p className="vdm-copied-note">Copied!</p>}

      <p className="mc-section-label">How to Use</p>
      <NumberedSteps steps={steps} />

      <Divider />

      <p className="mc-footnote">{disclaimer}</p>
    </BottomSheetModal>
  );
};

VoucherDetailModal.propTypes = {
  voucherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRestaurantClick: PropTypes.func,
};

VoucherDetailModal.defaultProps = {
  onRestaurantClick: () => {},
};

export default VoucherDetailModal;
